/** Rule windows use existing schoolDatePeriods, never a computed school year. */
export class SchoolRulePeriodError extends Error {
  constructor(message) {
    super(message);
    this.name = 'SchoolRulePeriodError';
    this.code = 'school-period-unavailable';
  }
}

export function isSchoolRulePeriodError(error) {
  return error?.code === 'school-period-unavailable';
}

/** Existing top-level school setting takes precedence over the legacy settings object. */
export function schoolRuleTimeZone(school = {}, fallback = 'Africa/Johannesburg') {
  const timeZone = school.timezone || school.settings?.timezone || fallback;
  try {
    if (typeof timeZone !== 'string') throw new Error('Invalid timezone');
    new Intl.DateTimeFormat('en', { timeZone }).format(new Date(0));
    return timeZone;
  } catch {
    throw new SchoolRulePeriodError('Correct the school timezone before evaluating its Time Periods.');
  }
}

export function normalizeRuleTimeframe(timeframe) {
  const aliases = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year', annually: 'year', annual: 'year', termly: 'term' };
  return aliases[timeframe] || timeframe;
}

export function needsSchoolRulePeriod(timeframe) {
  return !['day', 'week', 'month'].includes(normalizeRuleTimeframe(timeframe));
}

export function ruleDate(value) {
  if (value == null || value === '') return null;
  const date = value?.toDate?.() || (typeof value?.seconds === 'number'
    ? new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000) : new Date(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Historical eligibility uses incident time; capture time remains audit metadata. */
export function ruleRecordInRange(record, range) {
  const date = ruleDate(record?.issuedAt || record?.createdAt);
  return Boolean(date && date >= range.start && date <= range.end);
}

function partsAt(date, timeZone) {
  return Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(date).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
}

function midnight(parts, timeZone) {
  const desired = Date.UTC(parts.year, parts.month - 1, parts.day);
  let candidate = new Date(desired);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const actual = partsAt(candidate, timeZone);
    const represented = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
    if (represented === desired) return candidate;
    candidate = new Date(candidate.getTime() + desired - represented);
  }
  throw new SchoolRulePeriodError('The school period’s timezone boundaries could not be resolved.');
}

function savedDateParts(value, timeZone) {
  // Date-only values represent the school’s calendar date, not UTC midnight.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const check = new Date(Date.UTC(year, month - 1, day));
    if (check.getUTCFullYear() !== year || check.getUTCMonth() + 1 !== month || check.getUTCDate() !== day) return null;
    return { year, month, day };
  }
  const date = ruleDate(value);
  return date ? partsAt(date, timeZone) : null;
}

/** Display saved period dates in the same school calendar used by evaluation. */
export function schoolPeriodDateString(value, timeZone) {
  const parts = savedDateParts(value, timeZone);
  if (!parts) return '';
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

/** Keep the existing timestamp storage, using school midnight rather than browser midnight. */
export function schoolPeriodMidnight(dateString, timeZone) {
  if (dateString === '') return null;
  if (typeof dateString !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    throw new SchoolRulePeriodError('Enter a complete calendar date for the school period.');
  }
  const parts = savedDateParts(dateString, timeZone);
  if (!parts) throw new SchoolRulePeriodError('Enter a valid calendar date for the school period.');
  return midnight(parts, timeZone);
}

/** History-display compatibility only; rule evaluation always requires a saved period. */
export function resolveSchoolHistoryYear(periods, referenceDate, timeZone) {
  const reference = ruleDate(referenceDate);
  if (!reference || !Array.isArray(periods)) throw new SchoolRulePeriodError('School Time Periods could not be loaded.');
  const configuredYears = periods.filter(period => normalizeRuleTimeframe(period.typeId || period.type) === 'year'
    && period.active !== false && period.isActive !== false);
  if (configuredYears.length) {
    return { ...resolveSchoolRulePeriod(configuredYears, 'year', reference, timeZone), source: 'calendar' };
  }
  // Keep the established display cutoff only for schools without a configured
  // Year. A read failure, gap, overlap or unfinished active Year cannot use it.
  const year = reference.getUTCFullYear();
  return { start: new Date(Date.UTC(year, 0, 1)),
    end: new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999)), source: 'fallback' };
}

/** Time Periods are inclusive calendar dates; resolve the incident's period. */
export function resolveSchoolRulePeriod(periods, timeframe, referenceDate, timeZone = 'Africa/Johannesburg') {
  const type = normalizeRuleTimeframe(timeframe);
  const reference = ruleDate(referenceDate);
  if (!reference) throw new SchoolRulePeriodError('A valid incident date is required to resolve its school period.');
  if (!type || !Array.isArray(periods)) throw new SchoolRulePeriodError('School Time Periods could not be loaded.');
  const matches = [];
  for (const period of periods) {
    if (normalizeRuleTimeframe(period.typeId || period.type) !== type
      || period.isActive === false || period.active === false) continue;
    const startParts = savedDateParts(period.startDate, timeZone);
    const endParts = savedDateParts(period.endDate, timeZone);
    // A newly added row may still be awaiting its dates in the existing editor.
    if (!startParts || !endParts) continue;
    const start = midnight(startParts, timeZone);
    const nextDay = new Date(Date.UTC(endParts.year, endParts.month - 1, endParts.day + 1));
    const end = new Date(midnight({ year: nextDay.getUTCFullYear(), month: nextDay.getUTCMonth() + 1, day: nextDay.getUTCDate() }, timeZone).getTime() - 1);
    if (start > end) throw new SchoolRulePeriodError(`Correct the dates for ${period.name || type} in Time Periods before evaluating this rule.`);
    if (reference >= start && reference <= end) matches.push({ start, end, periodId: period.id, periodType: type });
  }
  if (matches.length !== 1) {
    throw new SchoolRulePeriodError(matches.length
      ? `Overlapping ${type} dates in Time Periods need review before evaluating this rule.`
      : `Set up ${type} dates covering the incident in Time Periods before evaluating this rule.`);
  }
  return matches[0];
}
